/**
 * WebSocket multiplexer.
 *
 * The Shakti server exposes five independent `/ws/*` channels:
 *
 *   - agent-activity  -> /ws/agent-activity
 *   - phase-stream    -> /ws/phase-stream
 *   - checkpoint-events -> /ws/diff-ready
 *   - metrics         -> /ws/agent-activity   (no dedicated server channel;
 *                                               we subscribe to the activity
 *                                               stream and filter client-side)
 *   - approvals       -> /ws/approvals
 *
 * This module:
 *   - Opens one socket per channel.
 *   - Runs independent reconnection loops with exponential backoff
 *     (500ms -> 30s, full-jitter).
 *   - Bridges each received message into `useWsStore` so React
 *     components can subscribe with `useWsStore((s) => s.lastMessage.xyz)`.
 *   - Exposes a singleton controlled by `initWebSocketClient()`.
 */

import { useAuthStore } from '../stores/auth';
import { useWsStore, type WsChannel, type WsConnectionStatus } from '../stores/ws';

const CHANNEL_PATHS: Record<WsChannel, string> = {
  'agent-activity': '/ws/agent-activity',
  'phase-stream': '/ws/phase-stream',
  'checkpoint-events': '/ws/diff-ready',
  metrics: '/ws/agent-activity',
  approvals: '/ws/approvals',
};

const CHANNELS: readonly WsChannel[] = Object.freeze([
  'agent-activity',
  'phase-stream',
  'checkpoint-events',
  'metrics',
  'approvals',
]);

const INITIAL_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 30_000;
const MAX_CONSECUTIVE_ERRORS = 10;

interface ChannelState {
  socket: WebSocket | null;
  /** Attempt count for backoff. Reset on a successful open. */
  attempts: number;
  /** Pending reconnect timer id. */
  timer: ReturnType<typeof setTimeout> | null;
  /** Prevents reconnection logic while disconnecting. */
  disposed: boolean;
  /**
   * Last server cursor observed on this channel. Replayed via `?cursor=`
   * on every reconnect so the client does not miss events during the
   * backoff window. Server is expected to honour `cursor` by resending
   * everything after it (or to simply start the live stream if the
   * cursor is unknown).
   */
  lastCursor: string | null;
}

export interface WebSocketClient {
  disconnect: () => void;
  reconnect: (channel?: WsChannel) => void;
}

let singleton: WebSocketClient | null = null;

/**
 * Compute the absolute ws:// URL for a channel, respecting the current
 * document origin (or the `VITE_SHAKTI_WS_BASE` override) and attaching
 * the auth token + org/project scope as query params when present.
 *
 * #118 (round-6 close-out) — when `wsTicket` is set in the auth store
 * the ticket takes precedence over the raw API key. The ticket is
 * minted by `POST /v1/auth/ws-ticket` and is bound to the (org,
 * optional project, 10min expiry) scope the backend enforces.
 */
function channelUrl(channel: WsChannel, cursor: string | null): string {
  const override = (import.meta.env.VITE_SHAKTI_WS_BASE ?? '').trim();
  const path = CHANNEL_PATHS[channel];

  const base =
    override.length > 0
      ? override.replace(/\/$/, '')
      : (() => {
          if (typeof window === 'undefined') return '';
          const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          return `${proto}//${window.location.host}`;
        })();

  const auth = useAuthStore.getState();
  const params = new URLSearchParams();
  // Prefer the ws-ticket when present; fall back to the raw API key
  // so existing dashboards keep working during rollout.
  if (auth.wsTicket) {
    params.set('token', auth.wsTicket);
  } else if (auth.token) {
    params.set('token', auth.token);
  }
  if (auth.organizationId) params.set('org', auth.organizationId);
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  return `${base}${path}${qs ? `?${qs}` : ''}`;
}

function fullJitterBackoff(attempt: number): number {
  const capped = Math.min(MAX_BACKOFF_MS, INITIAL_BACKOFF_MS * 2 ** attempt);
  return Math.floor(Math.random() * capped);
}

/**
 * Pull a server-assigned cursor out of a parsed WS frame. Cashfree-style
 * cursor-at-top-level and nested `payload.cursor` shapes are both honoured
 * so the extractor survives server-side schema tweaks without forcing a
 * client rebuild.
 */
function extractCursor(parsed: unknown): string | null {
  if (typeof parsed !== 'object' || parsed === null) return null;
  const top = (parsed as { cursor?: unknown }).cursor;
  if (typeof top === 'string' && top.length > 0) return top;
  const payload = (parsed as { payload?: unknown }).payload;
  if (typeof payload === 'object' && payload !== null) {
    const nested = (payload as { cursor?: unknown }).cursor;
    if (typeof nested === 'string' && nested.length > 0) return nested;
  }
  return null;
}

function setStatus(channel: WsChannel, status: WsConnectionStatus, error?: string): void {
  useWsStore.getState().setStatus(channel, status, error);
}

function recomputeAggregateStatus(): void {
  const store = useWsStore.getState();
  const channels = store.channels;
  const statuses = CHANNELS.map((ch) => channels[ch]?.status ?? 'closed');

  let aggregate: WsConnectionStatus = 'closed';
  if (statuses.some((s) => s === 'open')) {
    aggregate = 'open';
  } else if (statuses.some((s) => s === 'connecting')) {
    aggregate = 'connecting';
  } else if (statuses.some((s) => s === 'errored')) {
    aggregate = 'errored';
  }

  store.setConnection({ status: aggregate, lastChange: Date.now() });
}

function openChannel(state: Record<WsChannel, ChannelState>, channel: WsChannel): void {
  const slot = state[channel];
  if (slot.disposed) return;

  setStatus(channel, 'connecting');
  recomputeAggregateStatus();

  let socket: WebSocket;
  try {
    socket = new WebSocket(channelUrl(channel, slot.lastCursor));
  } catch (err) {
    const message = err instanceof Error ? err.message : 'unknown error';
    scheduleReconnect(state, channel, `construction failed: ${message}`);
    return;
  }

  slot.socket = socket;

  socket.addEventListener('open', () => {
    slot.attempts = 0;
    setStatus(channel, 'open');
    recomputeAggregateStatus();
  });

  socket.addEventListener('message', (event: MessageEvent<string>) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(event.data);
    } catch {
      parsed = { raw: event.data };
    }
    // Track the server-assigned cursor so reconnects can resume after the
    // last successfully delivered event. Frames of shape
    // `{ cursor?: string, payload?: { cursor?: string } }` are both honoured.
    const cursor = extractCursor(parsed);
    if (cursor) {
      slot.lastCursor = cursor;
    }
    useWsStore.getState().pushMessage(channel, {
      receivedAt: Date.now(),
      payload: parsed,
    });
  });

  socket.addEventListener('error', () => {
    setStatus(channel, 'errored', 'socket error');
  });

  socket.addEventListener('close', (event: CloseEvent) => {
    slot.socket = null;
    const reason = event.reason || `close code ${event.code}`;
    scheduleReconnect(state, channel, reason);
  });
}

function scheduleReconnect(
  state: Record<WsChannel, ChannelState>,
  channel: WsChannel,
  reason: string,
): void {
  const slot = state[channel];
  if (slot.disposed) return;

  slot.attempts = Math.min(slot.attempts + 1, MAX_CONSECUTIVE_ERRORS);
  setStatus(channel, 'closed', reason);
  recomputeAggregateStatus();

  const delay = fullJitterBackoff(slot.attempts);

  if (slot.timer) {
    clearTimeout(slot.timer);
  }
  slot.timer = setTimeout(() => {
    slot.timer = null;
    openChannel(state, channel);
  }, delay);
}

function teardownChannel(slot: ChannelState): void {
  slot.disposed = true;
  if (slot.timer) {
    clearTimeout(slot.timer);
    slot.timer = null;
  }
  if (slot.socket) {
    try {
      slot.socket.close(1000, 'client shutdown');
    } catch {
      /* ignore — browser will GC. */
    }
    slot.socket = null;
  }
}

/**
 * Initialise (or reuse) the singleton WebSocket client.
 *
 * Safe to call multiple times — subsequent calls return the already
 * running client so StrictMode double-mounts do not leak sockets.
 */
export function initWebSocketClient(): WebSocketClient {
  if (singleton) return singleton;

  const state: Record<WsChannel, ChannelState> = {
    'agent-activity': { socket: null, attempts: 0, timer: null, disposed: false, lastCursor: null },
    'phase-stream': { socket: null, attempts: 0, timer: null, disposed: false, lastCursor: null },
    'checkpoint-events': { socket: null, attempts: 0, timer: null, disposed: false, lastCursor: null },
    metrics: { socket: null, attempts: 0, timer: null, disposed: false, lastCursor: null },
    approvals: { socket: null, attempts: 0, timer: null, disposed: false, lastCursor: null },
  };

  CHANNELS.forEach((channel) => openChannel(state, channel));

  singleton = {
    disconnect(): void {
      CHANNELS.forEach((channel) => teardownChannel(state[channel]));
      singleton = null;
    },
    reconnect(channel?: WsChannel): void {
      const targets: readonly WsChannel[] = channel ? [channel] : CHANNELS;
      targets.forEach((ch) => {
        const slot = state[ch];
        if (slot.socket) {
          try {
            slot.socket.close(1000, 'manual reconnect');
          } catch {
            /* ignore */
          }
        }
        slot.attempts = 0;
        openChannel(state, ch);
      });
    },
  };

  return singleton;
}

export { CHANNELS };
